import { Injectable } from '@angular/core';
import {
    DossierPrestationPieceJustificativeModelDto,
    DossierPrestationRemarquesPieceJustificativeModelDtoInput,
    DossierPrestationValiderPieceJustificativeModelDtoInput,
    FichierModelInput,
    MutationCreerDemandeDeTelechargementArgs,
    MutationEnregistrerRemarquesPieceJustificativeArgs,
    MutationExtensionPieceJustificativeValideArgs,
    MutationTeleverserPieceJustificativeArgs,
    PieceJustificativeBenefModel
} from 'src/graphql/generated-types';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { UserState } from 'src/app/_user/store/user/user.state';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import { KalDialogConfig, KalDialogService, KalSnackbarConfig, KalSnackbarService } from '@kalidea/kaligraphi';
import { IGNORE_ALL_GRAPHQL_ERRORS } from 'src/app/_core/graphql.module';
import { AppFile, createNewFile } from 'src/app/utils/file.utils';
import { SimpleFileUploadComponent } from 'src/app/_shared/components/simple-file-upload/simple-file-upload.component';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';

export enum CodesDownload {
    PJ_BENEF = 'PJ_BENEF',
    PJ_DOSSIER = 'PJ_DOSSIER',
    PJ_COMPTABLE = 'PJ_COMPTABLE'
}

export interface VoucherCriteria {
    idElement: number;
    CodeDownload: keyof typeof CodesDownload;
}

@Injectable({
    providedIn: 'root'
})
export class VoucherService {

    readonly splitExtension = ';';

    constructor(private readonly graphqlService: GraphqlService,
                private readonly store: Store,
                private readonly dialogService: KalDialogService,
                private readonly snackbarService: KalSnackbarService) {
    }

    private _loading = new BehaviorSubject(false);

    get loading(): boolean {
        return this._loading.getValue();
    }

    set loading(loading: boolean) {
        this._loading.next(loading);
    }

    get loading$(): Observable<boolean> {
        return this._loading.asObservable();
    }

    openVoucher(criteria: VoucherCriteria): Observable<any> {
        this.loading = true;
        return this.graphqlService.mutateOne<'CreerDemandeDeTelechargement', MutationCreerDemandeDeTelechargementArgs>({
            mutation: gql`
                mutation CreerDemandeDeTelechargement($contexte: ContextInfosInput, $idElement: Int, $CodeDownload: String) {
                    CreerDemandeDeTelechargement(contexte: $contexte, idElement: $idElement, CodeDownload: $CodeDownload)
                }
            `,
            context: {[IGNORE_ALL_GRAPHQL_ERRORS]: true},
            variables: {
                ...criteria
            }
        }).pipe(
            tap((fileDownloadToken) => {
                this.loading = false;
                const ceName = this.store.selectSnapshot(UserState.pathsCE).NomCE;
                const downloadUrl = this.store.selectSnapshot(UserState.fileDownloadUrl);
                window.open(`${downloadUrl}/${fileDownloadToken}?type=PJ&nomCE=${ceName}`);
            }),
            catchError(err => {
                this.loading = false;
                this.snackbarService.open(new KalSnackbarConfig({
                    title: 'Le document est introuvable.'
                }));
                return throwError(err);
            })
        );
    }

    openVoucherBeneficiary(voucher: PieceJustificativeBenefModel): Observable<any> {
        return this.openVoucher({
            idElement: voucher.IsPJBenef ? voucher.IdPJBenef : voucher.IdPJDossier,
            CodeDownload: voucher.IsPJBenef ? CodesDownload.PJ_BENEF : CodesDownload.PJ_DOSSIER
        });
    }

    // Update ou Create le commentaire, seulement le Graphql
    commentUpdate(remarques: DossierPrestationRemarquesPieceJustificativeModelDtoInput): Observable<string> {
        this.loading = true;
        return this.graphqlService.mutateOne<'EnregistrerRemarquesPieceJustificative', MutationEnregistrerRemarquesPieceJustificativeArgs>({
            mutation: gql`
                mutation EnregistrerRemarquesPieceJustificative(
                    $contexte: ContextInfosInput,
                    $remarques: DossierPrestationRemarquesPieceJustificativeModelDTOInput,
                ){
                    EnregistrerRemarquesPieceJustificative(
                        contexte: $contexte,
                        remarques : $remarques,
                    )
                }
            `,
            variables: {
                remarques
            }
        }).pipe(
            tap(() => this.loading = false),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    // Quand on a sélectionné un fichier
    importVoucherSelect(appFile: AppFile, criteria: DossierPrestationValiderPieceJustificativeModelDtoInput, simpleFileUpload: SimpleFileUploadComponent): Observable<any> {
        // si on a pas d'appFile ça veut dire que le fichier n'est pas valide
        if (!appFile) {
            return;
        }

        this.loading = true;
        const fileUploaded = createNewFile(appFile);

        return this.importVoucherExtension(fileUploaded.Extension).pipe(
            switchMap(data => {
                this.loading = false;
                if (!data) {
                    return of(null);
                }
                const extensionList = data.split(this.splitExtension);
                return this.importVoucherDialog(new KalDialogConfig<ConfirmDialogData>({
                    title: 'Importer une pièce justificative',
                    data: {
                        paragraphs: [`L'extension du fichier n'est pas valide.`, `Les extensions autorisées sont : ${extensionList.join(', ')}.`],
                        submitLabel: 'Importer une nouvelle pièce jointe',
                        cancelLabel: 'Fermer'
                    }
                }), simpleFileUpload);
            }),
            filter(data => !data),
            switchMap(() => {
                this.loading = true;
                return this.importVoucherSave(criteria, fileUploaded)
            }),
            tap(Id => {
                this.loading = false;
                this.snackbarService.open(new KalSnackbarConfig({
                    title: 'La pièce justificative ' + (Id ? 'a été' : 'n\'a pas pu être') + ' importée.'
                }));
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    // Affiche une pop-in
    private importVoucherDialog(config: KalDialogConfig, simpleFileUpload: SimpleFileUploadComponent): Observable<ConfirmDialogData> {
        config.hasBackdrop = true;
        config.backdropClass = 'cdk-overlay-dark-backdrop';
        return this.dialogService.open(ConfirmDialogComponent, config).afterClosed.pipe(
            filter((result: ConfirmDialogData) => !!result?.accepted),
            tap(() => simpleFileUpload.click())
        );
    }

    // Check l'extension du fichier importé, seulement le Graphql
    private importVoucherExtension(extension: string): Observable<string> {
        return this.graphqlService.mutateOne<'ExtensionPieceJustificativeValide', MutationExtensionPieceJustificativeValideArgs>({
            mutation: gql`
                mutation ExtensionPieceJustificativeValide(
                    $contexte: ContextInfosInput,
                    $extensionPJ: String
                ){
                    ExtensionPieceJustificativeValide(
                        contexte: $contexte,
                        extensionPJ : $extensionPJ
                    )
                }`,
            variables: {
                extensionPJ: extension
            }
        });
    }

    private importVoucherSave(pieceJustificative: DossierPrestationValiderPieceJustificativeModelDtoInput, fileUploadValue: FichierModelInput): Observable<DossierPrestationPieceJustificativeModelDto> {
        return this.graphqlService.mutateOne<'TeleverserPieceJustificative', MutationTeleverserPieceJustificativeArgs>({
            mutation: gql`
                mutation TeleverserPieceJustificative(
                    $contexte: ContextInfosInput,
                    $pieceJustificative: DossierPrestationValiderPieceJustificativeModelDTOInput
                    $fichier: String,
                    $nomFichier: String
                ){
                    TeleverserPieceJustificative(
                        contexte: $contexte,
                        pieceJustificative: $pieceJustificative,
                        fichier: $fichier,
                        nomFichier: $nomFichier
                    ){
                        Id
                    }
                }
            `,
            variables: {
                pieceJustificative,
                fichier: fileUploadValue.FileBytes,
                nomFichier: fileUploadValue.Filename
            }
        });
    }
}
